// src/pages/AboutPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './AboutPage.css';
// AboutHeader n'est plus importé pour l'intro de la page
import MissionSection from '../components/about/MissionSection';
import ValuesSection from '../components/about/ValuesSection';
import WhyUsSection from '../components/about/WhyUsSection';
import HistorySection from '../components/about/HistorySection';
import TeamSection from '../components/about/TeamSection';

function AboutPage() {
  const navigate = useNavigate();
  const siteUrl = "https://infocolab.com";
  const pageUrl = `${siteUrl}/a-propos`;
  const pageTitle = "À Propos de INFO COLAB - Notre Agence, Équipe et Valeurs";
  const pageDescription = "Découvrez l'équipe INFO COLAB, notre histoire, notre mission et les valeurs qui animent notre agence web.";
  const ogImageUrl = `${siteUrl}/images/og-about.jpg`; // Crée cette image

  const introTitle = "À Propos";
  const introText = "Apprenez à nous connaître : notre parcours, notre équipe passionnée et les valeurs qui guident chacun de nos projets web.";


  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="INFO COLAB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      {/* Le conteneur global pour la page */}
      <div className="container">
        {/* Conteneur spécifique à la page À Propos, qui a déjà un padding via CSS */}
        <div className="about-page-container">
          <section className="about-page-intro-section">
            <h1>{introTitle}</h1>
            <p className="intro">{introText}</p>
          </section>

          <MissionSection />
          <TeamSection />

          <div style={{ marginTop: 0, marginBottom: 60 }}>
            <div className="card" style={{ maxWidth: 800, margin: '0 auto', border: '1px solid #3b82f6', borderRadius: 10, padding: 30, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', textAlign: 'center' }}>
              <h3 style={{ marginTop: 0, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, fontSize: '1.5rem' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                Rejoindre l'équipe
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem', margin: '16px auto', maxWidth: 600 }}>
                Vous souhaitez aller plus loin et devenir partenaire ? Postulez en quelques clics via votre compte existant.
              </p>
              <div style={{ display: 'flex', gap: 16, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn" style={{ border: 'none', background: 'white', color: '#1e40af', fontWeight: 600, padding: '12px 24px', fontSize: '1rem', cursor: 'pointer', borderRadius: '8px' }} onClick={() => navigate('/candidature', { state: { role: 'commercial', skipIntro: true } })}>
                  Devenir Commercial
                </button>
                <button className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', padding: '12px 24px', fontSize: '1rem', cursor: 'pointer', borderRadius: '8px' }} onClick={() => navigate('/candidature', { state: { role: 'developpeur', skipIntro: true } })}>
                  Devenir Développeur
                </button>
              </div>
            </div>
          </div>
          <section className="about-section why-choose-infocolab-section">
            <h2>Pourquoi Choisir INFO COLAB ?</h2>
            <p>
              Choisir INFO COLAB, c'est opter pour une agence web qui allie expertise technique, créativité et une véritable approche collaborative. Nous ne nous contentons pas de livrer un produit ; nous construisons des relations durables et accompagnons nos clients vers la réussite.
            </p>
            {/* Nouvelle structure moderne pour "Pourquoi Choisir INFO COLAB" */}
            <div className="why-choose-modern-grid">
              <div className="why-choose-card">
                <div className="card-icon-wrapper">
                  <span role="img" aria-label="cible">🎯</span>
                </div>
                <h3>Approche Personnalisée</h3>
                <p>Une stratégie sur mesure, centrée sur vos besoins spécifiques pour atteindre vos objectifs.</p>
              </div>

              <div className="why-choose-card">
                <div className="card-icon-wrapper">
                  <span role="img" aria-label="engrenage">⚙️</span>
                </div>
                <h3>Expertise Full-stack</h3>
                <p>Une maîtrise complète du développement pour des solutions robustes et évolutives.</p>
              </div>

              <div className="why-choose-card">
                <div className="card-icon-wrapper">
                  <span role="img" aria-label="check">✅</span>
                </div>
                <h3>Qualité & Délais</h3>
                <p>Un engagement ferme envers l'excellence technique et le respect de votre planning.</p>
              </div>

              <div className="why-choose-card">
                <div className="card-icon-wrapper">
                  <span role="img" aria-label="bulle">💬</span>
                </div>
                <h3>Transparence</h3>
                <p>Une communication claire et un suivi régulier pour une collaboration en toute confiance.</p>
              </div>

              <div className="why-choose-card">
                <div className="card-icon-wrapper">
                  <span role="img" aria-label="idée">💡</span>
                </div>
                <h3>Innovation</h3>
                <p>Une passion pour les nouvelles technologies afin de vous offrir des solutions d'avant-garde.</p>
              </div>
            </div>
          </section>
          <ValuesSection />
        </div>
      </div>
    </>
  );
}

export default AboutPage;